import { useMemo } from "react";

const PLACEMENT_NAMES: Record<string, string> = {
  homepage_banner: "Homepage Banner",
  in_feed: "In-Feed Native",
  article_inline: "Article Inline",
  sidebar: "Sidebar Spotlight",
  sector_mid: "Sector Mid Banner",
  nav_menu: "Navigation Spotlight",
  sector_inline: "Sector In-Feed",
  research_spotlight: "Research Spotlight",
  premium_insight: "Premium Insight",
};

const AdChip = () => (
  <span className="font-mono text-[9px] text-gray-500 uppercase tracking-widest border border-white/15 rounded px-1.5 py-0.5 shrink-0">Ad</span>
);

export const AdLivePreview = ({ file, placement, name, advertiser }: {
  file: File; placement: string; name: string; advertiser: string;
}) => {
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  const title = name.trim() || "Your campaign headline";
  const brand = advertiser.trim() || "Your brand";
  const isVideo = file.type.startsWith("video/");
  const Media = ({ className }: { className: string }) =>
    isVideo
      ? <video src={url} autoPlay loop muted playsInline preload="auto" aria-label={title} className={className} />
      : <img src={url} alt={title} className={className} />;

  let unit: React.ReactNode;
  if (placement === "homepage_banner") {
    unit = (
      <div className="relative rounded-xl overflow-hidden">
        <span className="absolute top-2 right-2 z-10 font-mono text-[9px] uppercase tracking-widest bg-black/70 text-gray-300 px-1.5 py-0.5 rounded">
          Ad · {brand}
        </span>
        <Media className="w-full h-auto max-h-[200px] object-cover" />
      </div>
    );
  } else if (placement === "in_feed" || placement === "sector_inline") {
    unit = (
      <div className="max-w-sm flex flex-col bg-[#121212] border border-[#F59E0B]/60 rounded-lg overflow-hidden">
        <div className="relative aspect-[16/9] overflow-hidden bg-[#1A1A1A]">
          <Media className="w-full h-full object-cover" />
          <span className="absolute bottom-3 left-0 z-10 bg-[#241c08]/95 border-l-[3px] border-[#F59E0B] px-3 py-1.5 font-heading font-bold text-[#FBBF24] text-[10px] tracking-[0.18em] uppercase shadow-lg">
            Sponsored
          </span>
        </div>
        <div className="p-4">
          <h3 className="font-heading font-semibold text-white text-base leading-snug line-clamp-2">{title}</h3>
          <div className="flex items-center justify-between gap-2 pt-3">
            <p className="font-mono text-[10px] text-gray-500 uppercase tracking-wide truncate">{brand}</p>
            <AdChip />
          </div>
        </div>
      </div>
    );
  } else if (placement === "research_spotlight") {
    unit = (
      <div className="max-w-sm flex flex-col bg-[#020306] border border-[#F59E0B]/60 rounded-[10px] overflow-hidden">
        <div className="relative aspect-[16/10] overflow-hidden bg-[#1A1A1A]">
          <Media className="w-full h-full object-cover" />
          <span className="absolute top-3 left-3 z-10 inline-flex items-center rounded-md border border-[#F59E0B]/70 bg-[#030407]/90 px-2.5 py-1 font-mono text-[10px] font-medium text-[#FBBF24]">
            SPONSORED
          </span>
        </div>
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-white/10">
          <div className="min-w-0">
            <h3 className="font-heading font-semibold text-white text-sm leading-snug line-clamp-1">{title}</h3>
            <p className="font-mono text-[10px] text-gray-500 uppercase tracking-wide truncate mt-0.5">{brand}</p>
          </div>
          <AdChip />
        </div>
      </div>
    );
  } else if (placement === "premium_insight") {
    unit = (
      <div className="max-w-sm relative flex flex-col aspect-[7/6] bg-[#020306] border border-white/10 rounded-[10px] overflow-hidden">
        <Media className="absolute inset-0 w-full h-full object-cover" />
        <span className="absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-black/55 to-transparent" aria-hidden />
        <div className="absolute top-3 inset-x-4 flex items-center justify-between gap-2">
          <span className="font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-[#A78BFA]">Premium Insight</span>
          <span className="inline-flex items-center rounded-md border border-[#F59E0B]/70 bg-[#030407]/90 px-2 py-0.5 font-mono text-[9px] font-medium text-[#FBBF24]">SPONSORED</span>
        </div>
        <span className="absolute bottom-3 right-4 font-mono text-[9px] text-gray-300 uppercase tracking-widest border border-white/25 bg-black/55 rounded px-1.5 py-0.5">Ad</span>
      </div>
    );
  } else if (placement === "article_inline") {
    unit = (
      <div className="max-w-xl flex gap-4 items-center bg-[#121212] border border-[#F59E0B]/60 rounded-lg overflow-hidden p-3 sm:p-4">
        <Media className="w-28 sm:w-40 aspect-video object-cover rounded shrink-0" />
        <div className="min-w-0 flex-1">
          <span className="font-mono text-[9px] text-[#FBBF24] uppercase tracking-[0.18em] font-bold">Sponsored</span>
          <h4 className="font-heading font-semibold text-white text-sm sm:text-base leading-snug line-clamp-2 mt-1">{title}</h4>
          <p className="font-mono text-[10px] text-gray-500 uppercase tracking-wide truncate mt-1.5">{brand}</p>
        </div>
        <span className="self-start"><AdChip /></span>
      </div>
    );
  } else if (placement === "nav_menu") {
    unit = (
      <div className="max-w-md flex items-center gap-3.5 rounded-lg border border-[#F59E0B]/35 bg-white/[0.02] px-3 py-2.5">
        <Media className="w-24 aspect-video object-cover rounded shrink-0" />
        <span className="min-w-0 flex-1">
          <span className="block font-mono text-[9px] text-[#FBBF24] uppercase tracking-[0.18em] font-bold">Sponsored</span>
          <span className="block text-[13px] font-semibold text-white leading-snug line-clamp-1 mt-0.5">{title}</span>
          <span className="block font-mono text-[10px] text-gray-500 uppercase tracking-wide truncate mt-0.5">{brand}</span>
        </span>
        <AdChip />
      </div>
    );
  } else {
    unit = (
      <div className={`${placement === "sector_mid" ? "max-w-2xl" : "max-w-[300px]"} bg-[#121212] border border-[#F59E0B]/60 rounded-xl overflow-hidden`}>
        <Media className="w-full h-auto block" />
        <div className="flex items-center justify-between px-3 py-2 border-t border-white/5">
          <span className="font-mono text-[10px] text-gray-500 uppercase tracking-widest truncate">{brand}</span>
          <AdChip />
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 bg-[#0B0B0B] border border-white/10 rounded-lg p-4" data-testid="ad-live-preview">
      <p className="font-mono text-[10px] uppercase tracking-widest text-[#00E5FF] mb-3">
        Live preview · exactly how your {PLACEMENT_NAMES[placement] || placement} will appear on-site
      </p>
      {unit}
    </div>
  );
};
